import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import type { Me } from '../auth/me';
import { can } from '../auth/me';
import { TASKS_KEY, TASKS_RESOURCE, type Assignee } from './types';

/** The people a task can be given to (any active account), for whoever may give tasks to others. */
export function useAssignees(me: Me) {
  const canAssign = can(me, TASKS_RESOURCE, 'assign');
  return useQuery({
    queryKey: [...TASKS_KEY, 'assignees'],
    enabled: canAssign,
    staleTime: 60_000,
    queryFn: () => api<{ assignees: Assignee[] }>('/api/company-tasks/assignees'),
  });
}

/**
 * "Odpowiedzialny": anybody active for whoever may give tasks to others, otherwise only "nikt" or oneself. The person
 * chosen always sees the task (no grant needed).
 */
export function AssigneeField({
  me,
  value,
  currentName,
  onChange,
  label = 'Odpowiedzialny',
  compact = false,
}: {
  me: Me;
  value: string;
  /** The name of the person now responsible (shown when they are not on the list). */
  currentName?: string | null;
  onChange: (id: string) => void;
  label?: string;
  compact?: boolean;
}) {
  const list = useAssignees(me);
  const options: { id: string; name: string }[] = list.data?.assignees ?? [{ id: me.id, name: me.name }];
  if (value && !options.some((o) => o.id === value)) options.push({ id: value, name: currentName ?? 'Inna osoba' });
  const select = (
    <select aria-label={compact ? label : undefined} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— nikt —</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.id === me.id ? `${o.name} (ja)` : o.name}
        </option>
      ))}
    </select>
  );
  if (compact) {
    return <span className="ct-assignee">{select}</span>;
  }
  return (
    <label className="field">
      <span>{label}</span>
      {select}
    </label>
  );
}
